/**
 * Assets feature module — the asset-freshness reconcile sweep (Task 14).
 *
 * requestAssetSweep() fires a throttled `requestAssetManifest` on a visibility-change
 * trigger (installed only for the pages TimelineWebViewPanel hosts — see initBridge's
 * `assetSweep` option). diff() then reconciles Unity's answer against the web project:
 * hash mismatches request a targeted regen, web-only rows Unity deleted are dropped
 * locally, and an unrecognized shot triggers exactly one bounded re-diff via a full
 * project re-push (plan decision 2).
 *
 * 20260731 — Unity's responder now exists (TimelineWebViewPanel.HandleRequestAssetManifest),
 * but v1 is STRUCTURAL: its manifest carries NO thumbHash/videoHash. So the three staleness
 * branches in diff() still never fire and `requestShotRegen` is still never sent in production
 * — deliberately, and Unity deliberately has no handler for it. What is live is the structural
 * half: missing shots are dropped (subject to the MASS_DROP_REFUSAL_* bound) and unknown shots
 * request a re-push. Hash-based freshness is a follow-up, and it must add hashes to the manifest
 * AND to the sceneReady/thumbnailChanged push paths in the same rollout — the web only ever
 * learns a hash from a push, so a manifest-first rollout makes every shot look stale at once
 * (see TimelineWebApp/docs/architecture.md).
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative, diagLog } from '../transport';
import { useProjectStore } from '@/store/projectStore';

export interface AssetManifestShot {
  shotId: number;
  sceneId: number;
  thumbHash?: string;
  videoHash?: string;
  category?: string;
  hasStaticImage?: boolean;
}

export interface AssetManifestMessage {
  type: 'assetManifest';
  payload: { version: number; shots: AssetManifestShot[]; sceneShotOrder?: Record<string, number[]> };
}

export type AssetsMessage = AssetManifestMessage;

const MIN_SWEEP_INTERVAL_MS = 30_000; // spec §5: tunable

/**
 * Mass-drop refusal bound. dropShots() hard-deletes rows from the live view — no undo, no confirmation —
 * and the manifest is the ONLY evidence that a shot is gone. So a manifest degraded by a responder-side
 * failure (partial build, truncated message, wrong project) does not read as "degraded", it reads as
 * "Unity deleted everything", and the user's board empties.
 *
 * Unity's responder is deliberately all-or-nothing for exactly this reason; this bound is the web's own
 * guard against that contract regressing. Above it we assume the manifest is inconsistent rather than that
 * the deletion is real, and keep the view intact — the .previs on disk is unaffected either way, so the
 * cost of refusing is a stale view until the next full load, versus wiping a real project view if we
 * comply. Below it, the drop applies as designed: that is the reconcile doing its job.
 *
 * Tuning note: the contract tests bracket the FLOOR to {2, 3} (one drop of three must apply, three of three
 * must be refused) — a floor of 2 would pass them too — while the FRACTION is pinned exactly by the 20-shot
 * case. Change either and re-read those tests rather than assuming they still constrain what you meant.
 */
const MASS_DROP_REFUSAL_FLOOR = 3;      // never refuse a drop smaller than this…
const MASS_DROP_REFUSAL_FRACTION = 0.2; // …nor one smaller than this share of the web's shots

let sweepInFlight = false;
let lastSweepAt = 0;
let rediffedOnce = false;

/** Throttled trigger: at most one `requestAssetManifest` per MIN_SWEEP_INTERVAL_MS. */
export function requestAssetSweep(): void {
  const now = Date.now();
  if (sweepInFlight || now - lastSweepAt < MIN_SWEEP_INTERVAL_MS) return;
  sweepInFlight = true;
  lastSweepAt = now;
  rediffedOnce = false;
  enqueueSendToNative('requestAssetManifest', {});
  // No timeout, no pending promise: a manifest that never arrives = designed dormant no-op.
  // sweepInFlight resets on the next manifest OR when the interval re-opens:
  setTimeout(() => { sweepInFlight = false; }, MIN_SWEEP_INTERVAL_MS);
}

function diff(manifest: AssetManifestMessage['payload']): void {
  const project = useProjectStore.getState().project;
  if (!project) return;
  const webShots = new Map<number, { thumbHash?: string; videoHash?: string }>();
  for (const sc of project.scenes) for (const sh of sc.shots) webShots.set(sh.id, sh as never);
  const webShotCount = webShots.size; // before the loop deletes the matched ones
  const staleIds: number[] = [];
  let unknownInManifest = false;
  for (const m of manifest.shots) {
    const w = webShots.get(m.shotId);
    if (!w) { unknownInManifest = true; continue; }
    if ((m.thumbHash && w.thumbHash && m.thumbHash !== w.thumbHash) ||
        (m.videoHash && w.videoHash && m.videoHash !== w.videoHash) ||
        (m.thumbHash && !w.thumbHash)) staleIds.push(m.shotId);
    webShots.delete(m.shotId);
  }
  if (staleIds.length) enqueueSendToNative('requestShotRegen', { shotIds: staleIds });
  // web-only rows: Unity deleted them → drop locally (spec §5), unless there are so many that the
  // manifest is likelier to be broken than the shots to be gone — see MASS_DROP_REFUSAL_* above.
  if (webShots.size) {
    const dropIds = [...webShots.keys()];
    const refusalBound = Math.max(MASS_DROP_REFUSAL_FLOOR, Math.ceil(MASS_DROP_REFUSAL_FRACTION * webShotCount));
    if (dropIds.length >= refusalBound) {
      const detail = `refusing to drop ${dropIds.length} of ${webShotCount} shots (bound ${refusalBound}): ` +
        'manifest is suspected partial or inconsistent rather than a real deletion — live view left intact';
      console.warn(`[Assets] ${detail}`);
      diagLog('Assets', detail); // …and into Unity's console, where this feature is actually diagnosed
      // Refusal on its own would strand the phantom rows forever: the next sweep re-refuses, and the
      // repush branch below only fires on unknownInManifest, which a too-small manifest does not set. So
      // the refusal and the repush are one corrective pair — the repush rebuilds the store from the
      // .previs truthfully, which is the only thing that can settle who is right. Shares rediffedOnce with
      // the branch below so a sweep still emits at most ONE requestProjectRepush no matter which branches
      // fire, and Unity's own 10s / 5-per-presentation limit backstops it.
      if (!rediffedOnce) {
        rediffedOnce = true;
        enqueueSendToNative('requestProjectRepush', {});
      }
    } else {
      diagLog('Assets', `dropping rows deleted in Unity: ${dropIds.join(',')}`);
      useProjectStore.getState().dropShots(dropIds);
    }
  }
  if (unknownInManifest && !rediffedOnce) {
    rediffedOnce = true;
    enqueueSendToNative('requestProjectRepush', {}); // plan decision 2: Unity re-push → setSqlitePath refresh:true → next manifest re-diffs
  }
}

export const assetsHandlers = {
  assetManifest: (msg: AssetManifestMessage) => { sweepInFlight = false; diff(msg.payload); },
} satisfies HandlerMap;

let triggersInstalled = false;
/**
 * Install the visibility-change sweep trigger. Called only for the pages TimelineWebViewPanel actually
 * hosts (see initBridge's `assetSweep` option) — the panel is the only Unity host that answers
 * `requestAssetManifest`, so installing it elsewhere just emits a verb nobody handles.
 *
 * @param signal optional AbortSignal, wired to the bridge's controller following the same pattern as
 *   initBridge's balance-refresh listener. Nothing calls `bridgeAbort.abort()` today, so this buys no
 *   teardown yet — it means the listener is already covered the day an abort site exists, rather than
 *   being the one bare addEventListener left outliving the bridge in the kept-alive embedded WebView.
 */
export function installAssetSweepTriggers(signal?: AbortSignal): void {
  if (triggersInstalled) return;
  triggersInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestAssetSweep();
  }, { signal });
}

/** Test-only: reset throttle/in-flight/rediff state so contract tests are order-independent.
 * Also re-arms the trigger once-guard; a test that re-installs must have aborted the signal it
 * passed the previous time, or the old listener stays attached and the sweep double-fires. */
export function __resetAssetSweepForTests(): void {
  sweepInFlight = false;
  lastSweepAt = 0;
  rediffedOnce = false;
  triggersInstalled = false;
}
